import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Autocomplete,
  TextField,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  GridViewRounded,
  PlayArrowRounded,
  FileUploadRounded,
  DownloadRounded,
  ClearRounded,
  AddRounded,
  RemoveCircleOutlineRounded,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useCompanies } from "../../Query/useCompanies";
import {
  useStartGridSearch,
  useGridSearchStatus,
} from "../../Query/useRetrievalTest";
import { fetchQuestionListsForEvaluation } from "../../Api/questions";
import { useTranslation } from "react-i18next";
import { QuestionItem } from "../../Models/question";
import { Company } from "../../Models/company";
import { GridSearchComboResult } from "../../Api/retrievalTest";

interface ParamDefinition {
  key: string;
  label: string;
  type: "number" | "boolean";
  defaultHint: string;
}

const GRID_PARAMS: ParamDefinition[] = [
  {
    key: "default_dense_company_data_retrieval_limit",
    label: "Dense Limit",
    type: "number",
    defaultHint: "default: 4",
  },
  {
    key: "hybrid_search_enabled",
    label: "Hybrid Enabled",
    type: "boolean",
    defaultHint: "default: false",
  },
  {
    key: "hybrid_search_alpha",
    label: "Hybrid Alpha",
    type: "number",
    defaultHint: "default: 0.5",
  },
  {
    key: "reranker_enabled",
    label: "Reranker Enabled",
    type: "boolean",
    defaultHint: "default: false",
  },
  {
    key: "reranker_multiplier",
    label: "Rerank Multiplier",
    type: "number",
    defaultHint: "default: 3",
  },
  {
    key: "company_data_similarity_threshold",
    label: "Similarity Threshold",
    type: "number",
    defaultHint: "default: 0.35",
  },
];

type ParamGrid = Record<string, (number | boolean)[]>;

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: color.secondary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: color.secondary,
    },
  },
  "& .MuiInputBase-input": { color: color.primary_dark },
  "& .MuiInputLabel-root": { color: color.gray500 },
  "& .MuiInputLabel-root.Mui-focused": { color: color.secondary },
};

const GridSearchTest: React.FC = () => {
  const { t } = useTranslation("retrievalTest");

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedQuestionList, setSelectedQuestionList] =
    useState<QuestionItem | null>(null);
  const [questionLists, setQuestionLists] = useState<QuestionItem[]>([]);
  const [questionListsLoading, setQuestionListsLoading] = useState(false);
  const [paramGrid, setParamGrid] = useState<ParamGrid>({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [testId, setTestId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [groundTruth, setGroundTruth] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [groundTruthError, setGroundTruthError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleGroundTruthUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        setGroundTruth(parsed);
        setGroundTruthError(false);
      } catch {
        setGroundTruth(null);
        setGroundTruthError(true);
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExportReport = () => {
    const reportMd = statusQuery.data?.report_md;
    if (!reportMd) return;
    const blob = new Blob([reportMd], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `grid_search_report_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const companiesQuery = useCompanies(1, 1000, "name", "asc", "");
  const startMutation = useStartGridSearch();
  const statusQuery = useGridSearchStatus(testId, isPolling);

  const companies: Company[] = companiesQuery.data?.companies ?? [];

  useEffect(() => {
    if (
      statusQuery.data?.status === "completed" ||
      statusQuery.data?.status === "error"
    ) {
      setIsPolling(false);
    }
  }, [statusQuery.data?.status]);

  useEffect(() => {
    if (!selectedCompany) {
      setQuestionLists([]);
      setSelectedQuestionList(null);
      return;
    }
    const loadQuestionLists = async () => {
      setQuestionListsLoading(true);
      try {
        const data = await fetchQuestionListsForEvaluation();
        setQuestionLists(data.question_lists);
      } catch {
        setQuestionLists([]);
      } finally {
        setQuestionListsLoading(false);
      }
    };
    loadQuestionLists();
  }, [selectedCompany]);

  const addParamValue = (paramKey: string, paramDef: ParamDefinition) => {
    const raw = (inputValues[paramKey] || "").trim();
    if (!raw) return;

    let value: number | boolean;
    if (paramDef.type === "boolean") {
      if (raw.toLowerCase() === "true") value = true;
      else if (raw.toLowerCase() === "false") value = false;
      else return;
    } else {
      value = parseFloat(raw);
      if (isNaN(value)) return;
    }

    setParamGrid((prev) => {
      const existing = prev[paramKey] || [];
      if (existing.includes(value)) return prev;
      return { ...prev, [paramKey]: [...existing, value] };
    });
    setInputValues((prev) => ({ ...prev, [paramKey]: "" }));
  };

  const removeParamValue = (paramKey: string, idx: number) => {
    setParamGrid((prev) => {
      const existing = [...(prev[paramKey] || [])];
      existing.splice(idx, 1);
      if (existing.length === 0) {
        const { [paramKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [paramKey]: existing };
    });
  };

  const totalCombinations = Object.values(paramGrid).reduce(
    (acc, values) => acc * (values.length || 1),
    Object.keys(paramGrid).length > 0 ? 1 : 0,
  );

  const handleRun = async () => {
    if (!selectedCompany || !selectedQuestionList || totalCombinations === 0)
      return;
    try {
      const result = await startMutation.mutateAsync({
        companyId: selectedCompany.id,
        questionListId: selectedQuestionList.id,
        paramGrid,
        groundTruth,
      });
      setTestId(result.test_id);
      setIsPolling(true);
    } catch {
      // snackbar handled by API layer
    }
  };

  const isRunning =
    isPolling ||
    statusQuery.data?.status === "running" ||
    statusQuery.data?.status === "queued";
  const isCompleted = statusQuery.data?.status === "completed";
  const isError = statusQuery.data?.status === "error";
  const canRun =
    !!selectedCompany &&
    !!selectedQuestionList &&
    totalCombinations > 0 &&
    !isRunning &&
    !startMutation.isPending;

  if (companiesQuery.isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  const comboResults: GridSearchComboResult[] =
    statusQuery.data?.combo_results || [];

  // Determine which param keys appear in results for table columns
  const resultParamKeys =
    comboResults.length > 0 ? Object.keys(comboResults[0].config) : [];
  const hasRecall = comboResults.some((r) => r.recall_metrics != null);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: color.primary,
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <GridViewRounded
              sx={{ fontSize: "2rem", color: color.secondary }}
            />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "1.75rem",
                color: color.secondary,
              }}
            >
              {t("retrievalTest.gridSearchTitle")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.85rem", color: color.gray500, mt: 1 }}>
            {t("retrievalTest.gridSearchDescription")}
          </Typography>
        </Box>

        {/* Selection Panel */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
              value={selectedCompany}
              onChange={(_, value) => {
                setSelectedCompany(value);
                setSelectedQuestionList(null);
                setTestId(null);
              }}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      color: color.primary_dark,
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("retrievalTest.selectCompany")}
                  size="small"
                  sx={textFieldSx}
                />
              )}
              disabled={isRunning}
            />
            <Autocomplete
              options={questionLists}
              getOptionLabel={(option) =>
                `${option.name}${option.category?.name ? ` (${option.category.name})` : ""}`
              }
              value={selectedQuestionList}
              onChange={(_, value) => {
                setSelectedQuestionList(value);
                setTestId(null);
              }}
              loading={questionListsLoading}
              disabled={!selectedCompany || isRunning}
              componentsProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      color: color.primary_dark,
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("retrievalTest.selectQuestionList")}
                  size="small"
                  sx={textFieldSx}
                />
              )}
            />
          </Box>
        </Paper>

        {/* Parameter Grid */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: color.primary_dark,
                fontSize: "1rem",
              }}
            >
              {t("retrievalTest.gridParamTitle")}
            </Typography>
            {totalCombinations > 0 && (
              <Chip
                label={t("retrievalTest.gridCombinations", {
                  count: totalCombinations,
                })}
                size="small"
                sx={{
                  backgroundColor: color.orange50,
                  color: color.orange600,
                  fontWeight: 600,
                  border: `1px solid ${color.orange200}`,
                }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: color.gray500, mb: 3 }}>
            {t("retrievalTest.gridParamDescription")}
          </Typography>

          {GRID_PARAMS.map((param) => (
            <Box
              key={param.key}
              sx={{
                mb: 2.5,
                p: 2,
                borderRadius: "10px",
                border: `1px solid ${color.gray200}`,
                backgroundColor: color.gray50,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: color.primary_dark,
                    fontSize: "0.9rem",
                  }}
                >
                  {param.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: color.gray400,
                    fontFamily: "'Roboto Mono', monospace",
                  }}
                >
                  ({param.defaultHint})
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                {param.type === "boolean" ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {[true, false].map((val) => {
                      const isSelected = (paramGrid[param.key] || []).includes(
                        val,
                      );
                      return (
                        <Button
                          key={String(val)}
                          variant={isSelected ? "contained" : "outlined"}
                          size="small"
                          onClick={() => {
                            setParamGrid((prev) => {
                              const existing = prev[param.key] || [];
                              if (existing.includes(val)) {
                                const filtered = existing.filter(
                                  (v) => v !== val,
                                );
                                if (filtered.length === 0) {
                                  const { [param.key]: _, ...rest } = prev;
                                  return rest;
                                }
                                return { ...prev, [param.key]: filtered };
                              }
                              return {
                                ...prev,
                                [param.key]: [...existing, val],
                              };
                            });
                          }}
                          disabled={isRunning}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            ...(isSelected
                              ? {
                                  backgroundColor: color.secondary,
                                  color: color.white,
                                  "&:hover": { backgroundColor: color.accent },
                                }
                              : {
                                  borderColor: color.gray300,
                                  color: color.gray500,
                                }),
                          }}
                        >
                          {String(val)}
                        </Button>
                      );
                    })}
                  </Box>
                ) : (
                  <>
                    <TextField
                      size="small"
                      type="number"
                      placeholder={t("retrievalTest.gridAddValue")}
                      value={inputValues[param.key] || ""}
                      onChange={(e) =>
                        setInputValues((prev) => ({
                          ...prev,
                          [param.key]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addParamValue(param.key, param);
                        }
                      }}
                      disabled={isRunning}
                      sx={{
                        width: 140,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: color.primary,
                        },
                        "& .MuiInputBase-input": {
                          color: color.primary_dark,
                          fontSize: "0.85rem",
                        },
                      }}
                      inputProps={{ step: "any" }}
                    />
                    <Tooltip title={t("retrievalTest.gridAddValue")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => addParamValue(param.key, param)}
                          disabled={
                            isRunning || !(inputValues[param.key] || "").trim()
                          }
                          sx={{ color: color.secondary }}
                        >
                          <AddRounded />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>

              {(paramGrid[param.key] || []).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(paramGrid[param.key] || []).map((val, idx) => (
                    <Chip
                      key={idx}
                      label={String(val)}
                      size="small"
                      onDelete={
                        isRunning
                          ? undefined
                          : () => removeParamValue(param.key, idx)
                      }
                      deleteIcon={
                        <RemoveCircleOutlineRounded sx={{ fontSize: "1rem" }} />
                      }
                      sx={{
                        backgroundColor: color.blue50,
                        color: color.secondary,
                        fontWeight: 600,
                        border: `1px solid ${color.blue200}`,
                        fontFamily: "'Roboto Mono', monospace",
                        fontSize: "0.8rem",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Paper>

        {/* Ground Truth Upload */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.primary,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              fontSize: "1rem",
              mb: 1,
            }}
          >
            {t("retrievalTest.groundTruth")}
          </Typography>
          <Typography variant="body2" sx={{ color: color.gray500, mb: 2 }}>
            {t("retrievalTest.groundTruthDescription")}
          </Typography>
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleGroundTruthUpload}
            style={{ display: "none" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileUploadRounded />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isRunning}
              sx={{
                borderColor: color.secondary,
                color: color.secondary,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: color.accent,
                  backgroundColor: color.blue50,
                },
              }}
            >
              {t("retrievalTest.uploadGroundTruth")}
            </Button>
            {groundTruth ? (
              <>
                <Chip
                  label={t("retrievalTest.groundTruthLoaded", {
                    count: Object.keys(groundTruth).length,
                  })}
                  size="small"
                  sx={{
                    backgroundColor: color.green50,
                    color: color.green700,
                    fontWeight: 500,
                    border: `1px solid ${color.green200}`,
                  }}
                />
                <Button
                  size="small"
                  startIcon={<ClearRounded />}
                  onClick={() => setGroundTruth(null)}
                  sx={{
                    color: color.gray500,
                    textTransform: "none",
                    minWidth: "auto",
                  }}
                >
                  {t("retrievalTest.groundTruthClear")}
                </Button>
              </>
            ) : (
              <Chip
                label={t("retrievalTest.groundTruthDefault")}
                size="small"
                sx={{
                  backgroundColor: color.blue50,
                  color: color.blue700,
                  fontWeight: 500,
                  border: `1px solid ${color.blue200}`,
                }}
              />
            )}
            {groundTruthError && (
              <Typography sx={{ color: color.red600, fontSize: "0.85rem" }}>
                {t("retrievalTest.groundTruthError")}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Run Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={!canRun}
            startIcon={<PlayArrowRounded />}
            sx={{
              backgroundColor: color.secondary,
              color: color.white,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              "&:hover": { backgroundColor: color.accent },
              "&.Mui-disabled": {
                backgroundColor: color.gray200,
                color: color.gray500,
              },
            }}
          >
            {startMutation.isPending
              ? "Starting..."
              : t("retrievalTest.gridRunTest", {
                  count: totalCombinations,
                })}
          </Button>
        </Box>

        {/* Progress */}
        {isRunning && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: "14px",
              border: `1px solid ${color.gray200}`,
              backgroundColor: color.primary,
            }}
          >
            <Typography sx={{ fontWeight: 600, color: color.secondary, mb: 1 }}>
              {t("retrievalTest.gridRunning")}
            </Typography>
            <LinearProgress
              sx={{
                mb: 1,
                borderRadius: 4,
                height: 8,
                backgroundColor: color.gray200,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: color.secondary,
                },
              }}
            />
            {statusQuery.data?.progress && (
              <Typography sx={{ fontSize: "0.85rem", color: color.gray500 }}>
                {t("retrievalTest.progress", {
                  progress: statusQuery.data.progress,
                })}
              </Typography>
            )}
          </Paper>
        )}

        {/* Error */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "14px" }}>
            {t("retrievalTest.errorMessage", {
              message:
                statusQuery.data?.error_message || t("retrievalTest.error"),
            })}
          </Alert>
        )}

        {/* Results */}
        {isCompleted && comboResults.length > 0 && (
          <>
            <Alert severity="success" sx={{ mb: 3, borderRadius: "14px" }}>
              {t("retrievalTest.gridCompleted", {
                count: comboResults.length,
              })}
            </Alert>

            {/* Export Report Button */}
            {statusQuery.data?.report_md && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadRounded />}
                  onClick={handleExportReport}
                  sx={{
                    borderColor: color.secondary,
                    color: color.secondary,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: color.accent,
                      backgroundColor: color.blue50,
                    },
                  }}
                >
                  {t("retrievalTest.exportReport")}
                </Button>
              </Box>
            )}

            {/* Results Table */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: "14px",
                border: `1px solid ${color.gray200}`,
                backgroundColor: color.primary,
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: color.primary_dark,
                  fontSize: "1rem",
                  p: 3,
                  pb: 0,
                }}
              >
                {t("retrievalTest.gridResultsTitle")}
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: color.secondary }}>
                      <TableCell
                        sx={{
                          color: color.white,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          border: "none",
                          py: 1.5,
                          pl: 3,
                          width: 50,
                        }}
                      >
                        #
                      </TableCell>
                      {resultParamKeys.map((key) => (
                        <TableCell
                          key={key}
                          sx={{
                            color: color.white,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            border: "none",
                            py: 1.5,
                          }}
                        >
                          {GRID_PARAMS.find((p) => p.key === key)?.label || key}
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          color: color.white,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          border: "none",
                          py: 1.5,
                          textAlign: "center",
                        }}
                      >
                        {t("retrievalTest.metricTotalSources")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: color.white,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          border: "none",
                          py: 1.5,
                          textAlign: "center",
                        }}
                      >
                        {t("retrievalTest.metricAvgSources")}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: color.white,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          border: "none",
                          py: 1.5,
                          textAlign: "center",
                        }}
                      >
                        {t("retrievalTest.metricUniqueDocuments")}
                      </TableCell>
                      {hasRecall && (
                        <>
                          <TableCell
                            sx={{
                              color: color.white,
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {t("retrievalTest.gridPassedTotal")}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: color.white,
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {t("retrievalTest.recallPassPct")}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: color.white,
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {t("retrievalTest.recallAvgRecall")}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: color.white,
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                              pr: 3,
                            }}
                          >
                            {t("retrievalTest.recallOverallRecall")}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comboResults.map((result, idx) => {
                      const rm = result.recall_metrics;
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            backgroundColor:
                              idx % 2 === 0 ? color.primary : color.gray50,
                            "&:hover": { backgroundColor: color.blue50 },
                            transition: "background-color 0.15s",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "'Roboto Mono', monospace",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: color.gray500,
                              border: "none",
                              py: 1.5,
                              pl: 3,
                            }}
                          >
                            {idx + 1}
                          </TableCell>
                          {resultParamKeys.map((key) => (
                            <TableCell
                              key={key}
                              sx={{
                                fontFamily: "'Roboto Mono', monospace",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: color.primary_dark,
                                border: "none",
                                py: 1.5,
                              }}
                            >
                              {String(result.config[key])}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              fontFamily: "'Roboto Mono', monospace",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: color.secondary,
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {result.metrics.total_sources}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "'Roboto Mono', monospace",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: color.secondary,
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {result.metrics.avg_sources_per_question}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "'Roboto Mono', monospace",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: color.secondary,
                              border: "none",
                              py: 1.5,
                              textAlign: "center",
                            }}
                          >
                            {result.metrics.unique_documents}
                          </TableCell>
                          {hasRecall && (
                            <>
                              <TableCell
                                sx={{
                                  fontFamily: "'Roboto Mono', monospace",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  color: rm
                                    ? color.primary_dark
                                    : color.gray400,
                                  border: "none",
                                  py: 1.5,
                                  textAlign: "center",
                                }}
                              >
                                {rm
                                  ? `${rm.passed}/${rm.total_evaluated}`
                                  : "—"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: "'Roboto Mono', monospace",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  color: rm
                                    ? rm.pass_pct >= 80
                                      ? color.green700
                                      : rm.pass_pct >= 50
                                        ? color.orange600
                                        : color.red600
                                    : color.gray400,
                                  border: "none",
                                  py: 1.5,
                                  textAlign: "center",
                                }}
                              >
                                {rm ? `${rm.pass_pct}%` : "—"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: "'Roboto Mono', monospace",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  color: rm
                                    ? rm.avg_recall >= 80
                                      ? color.green700
                                      : rm.avg_recall >= 50
                                        ? color.orange600
                                        : color.red600
                                    : color.gray400,
                                  border: "none",
                                  py: 1.5,
                                  textAlign: "center",
                                }}
                              >
                                {rm ? `${rm.avg_recall}%` : "—"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontFamily: "'Roboto Mono', monospace",
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  color: rm
                                    ? rm.overall_recall >= 80
                                      ? color.green700
                                      : rm.overall_recall >= 50
                                        ? color.orange600
                                        : color.red600
                                    : color.gray400,
                                  border: "none",
                                  py: 1.5,
                                  textAlign: "center",
                                  pr: 3,
                                }}
                              >
                                {rm ? `${rm.overall_recall}%` : "—"}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        {/* Empty state */}
        {!isRunning && !isCompleted && !isError && (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: "14px",
              border: `1px solid ${color.gray200}`,
              backgroundColor: color.primary,
              textAlign: "center",
            }}
          >
            <GridViewRounded
              sx={{ fontSize: "3rem", color: color.gray200, mb: 1 }}
            />
            <Typography sx={{ color: color.gray500, fontSize: "0.9rem" }}>
              {t("retrievalTest.gridNoResults")}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default GridSearchTest;
